import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Download, Search, ShieldAlert, Filter } from "lucide-react";

type DataType = "string" | "integer" | "decimal" | "boolean" | "date" | "datetime" | "UUID" | "enum" | "blob" | "json";
type Nullable = "yes" | "no";

interface DataField {
  id: string;
  name: string;
  alias: string;
  dataType: DataType;
  format: string;
  nullable: Nullable;
  isPHI: boolean;
  sourceSystem: string;
  targetSystem: string;
  description: string;
  validationRules: string;
  exampleValue: string;
  notes: string;
}

const EMPTY: Omit<DataField, "id"> = {
  name: "", alias: "", dataType: "string", format: "", nullable: "yes",
  isPHI: false, sourceSystem: "", targetSystem: "",
  description: "", validationRules: "", exampleValue: "", notes: "",
};

const DATA_TYPES: DataType[] = ["string", "integer", "decimal", "boolean", "date", "datetime", "UUID", "enum", "blob", "json"];

export default function DataDictionaryPanel() {
  const [fields, setFields] = useState<DataField[]>([]);
  const [editing, setEditing] = useState<DataField | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [phiOnly, setPhiOnly] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = fields.filter(f =>
    (!phiOnly || f.isPHI) &&
    (!search || f.name.toLowerCase().includes(search.toLowerCase()) ||
      f.description.toLowerCase().includes(search.toLowerCase()) ||
      f.sourceSystem.toLowerCase().includes(search.toLowerCase()))
  );

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (f: DataField) => { setEditing(f); setForm({ ...f }); setFormOpen(true); };

  const save = () => {
    if (!form.name.trim()) return;
    if (editing) {
      setFields(prev => prev.map(f => f.id === editing.id ? { ...form, id: editing.id } : f));
    } else {
      setFields(prev => [...prev, { ...form, id: crypto.randomUUID() }]);
    }
    setEditing(null);
    setForm(EMPTY);
    setFormOpen(false);
  };

  const del = (id: string) => setFields(prev => prev.filter(f => f.id !== id));

  const exportCsv = () => {
    const headers = ["Field Name", "Alias", "Type", "Format", "Nullable", "PHI", "Source", "Target", "Description", "Validation", "Example", "Notes"];
    const rows = fields.map(f => [f.name, f.alias, f.dataType, f.format, f.nullable, f.isPHI ? "YES" : "no", f.sourceSystem, f.targetSystem, f.description, f.validationRules, f.exampleValue, f.notes]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "data-dictionary.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const phiCount = fields.filter(f => f.isPHI).length;

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 border-b border-border px-5 py-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Data Dictionary</h2>
          <p className="text-[11px] text-muted-foreground">
            {fields.length > 0
              ? `${fields.length} fields · ${phiCount} PHI-flagged`
              : "Document every data field: type, source, target, constraints, PHI status."}
          </p>
        </div>
        <div className="flex gap-2">
          {fields.length > 0 && <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={exportCsv}><Download className="h-3 w-3" /> Export</Button>}
          <Button size="sm" className="h-7 gap-1 text-[11px]" onClick={openNew}><Plus className="h-3 w-3" /> Add Field</Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ scrollbarWidth: "thin" }}>

        {/* Edit form */}
        {formOpen && (
          <div className="border border-border rounded-lg p-4 bg-card space-y-3">
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{editing ? "Edit field" : "New field"}</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Field Name *</label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="patient_id" className="h-7 text-xs font-mono" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Alias / Label</label>
                <Input value={form.alias} onChange={e => setForm(f => ({ ...f, alias: e.target.value }))} placeholder="Patient ID" className="h-7 text-xs" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Data Type</label>
                <select value={form.dataType} onChange={e => setForm(f => ({ ...f, dataType: e.target.value as DataType }))}
                  className="w-full h-7 text-xs border border-input rounded-md px-2 bg-background text-foreground">
                  {DATA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Format</label>
                <Input value={form.format} onChange={e => setForm(f => ({ ...f, format: e.target.value }))} placeholder="MRN-XXXXXX" className="h-7 text-xs font-mono" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Nullable</label>
                <select value={form.nullable} onChange={e => setForm(f => ({ ...f, nullable: e.target.value as Nullable }))}
                  className="w-full h-7 text-xs border border-input rounded-md px-2 bg-background text-foreground">
                  <option value="yes">Yes</option>
                  <option value="no">No (required)</option>
                </select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.isPHI} onChange={e => setForm(f => ({ ...f, isPHI: e.target.checked }))} className="h-4 w-4" />
                  <span className="text-xs font-medium text-destructive">PHI / Sensitive</span>
                </label>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Source System</label>
                <Input value={form.sourceSystem} onChange={e => setForm(f => ({ ...f, sourceSystem: e.target.value }))} placeholder="MedTrack" className="h-7 text-xs" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Target System</label>
                <Input value={form.targetSystem} onChange={e => setForm(f => ({ ...f, targetSystem: e.target.value }))} placeholder="Salesforce HC" className="h-7 text-xs" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Example Value</label>
                <Input value={form.exampleValue} onChange={e => setForm(f => ({ ...f, exampleValue: e.target.value }))} placeholder="MRN-001234" className="h-7 text-xs font-mono" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Description</label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What this field represents" className="h-7 text-xs" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Validation Rules</label>
              <Input value={form.validationRules} onChange={e => setForm(f => ({ ...f, validationRules: e.target.value }))} placeholder="Must match ^MRN-\d{6}$, unique, not null" className="h-7 text-xs font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Notes</label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Migration notes, legacy quirks, known issues" className="h-7 text-xs" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(null); setForm(EMPTY); setFormOpen(false); }}>Cancel</Button>
              <Button size="sm" className="h-7 text-xs" onClick={save}>Save Field</Button>
            </div>
          </div>
        )}

        {/* Search + filters */}
        {fields.length > 0 && (
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search fields…" className="h-7 text-xs pl-8" />
            </div>
            <button onClick={() => setPhiOnly(v => !v)}
              className={`flex items-center gap-1.5 h-7 px-3 rounded-md border text-[11px] transition-colors ${phiOnly ? "bg-destructive/10 text-destructive border-destructive/30" : "border-border text-muted-foreground hover:border-muted-foreground/40"}`}>
              <ShieldAlert className="h-3 w-3" /> PHI only
            </button>
          </div>
        )}

        {/* Table */}
        {filtered.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Field</th>
                  <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Type</th>
                  <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Source → Target</th>
                  <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Description</th>
                  <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Null</th>
                  <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">PHI</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(field => (
                  <>
                    <tr key={field.id}
                      className="border-b border-border/50 hover:bg-muted/20 cursor-pointer"
                      onClick={() => setExpanded(expanded === field.id ? null : field.id)}>
                      <td className="px-3 py-2">
                        <div className="font-mono font-semibold text-foreground">{field.name}</div>
                        {field.alias && <div className="text-[10px] text-muted-foreground">{field.alias}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px] font-mono px-1.5">{field.dataType}</Badge>
                        {field.format && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{field.format}</div>}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground">
                        {field.sourceSystem && <span>{field.sourceSystem}</span>}
                        {field.sourceSystem && field.targetSystem && <span className="mx-1">→</span>}
                        {field.targetSystem && <span>{field.targetSystem}</span>}
                        {!field.sourceSystem && !field.targetSystem && "—"}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground max-w-xs truncate">{field.description || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[10px] font-mono ${field.nullable === "no" ? "text-amber-600" : "text-muted-foreground"}`}>
                          {field.nullable === "no" ? "req" : "null"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {field.isPHI && <Badge variant="outline" className="text-[9px] px-1 bg-destructive/10 text-destructive border-destructive/30"><ShieldAlert className="h-2.5 w-2.5" /></Badge>}
                      </td>
                      <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(field)} className="text-muted-foreground hover:text-foreground text-[10px] px-1">edit</button>
                          <button onClick={() => del(field.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
                        </div>
                      </td>
                    </tr>
                    {expanded === field.id && (
                      <tr key={`${field.id}-expanded`} className="bg-muted/10 border-b border-border/50">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="grid grid-cols-3 gap-4 text-[11px]">
                            {field.validationRules && <div><span className="font-mono text-muted-foreground uppercase text-[9px] block mb-0.5">Validation</span>{field.validationRules}</div>}
                            {field.exampleValue && <div><span className="font-mono text-muted-foreground uppercase text-[9px] block mb-0.5">Example</span><code className="font-mono">{field.exampleValue}</code></div>}
                            {field.notes && <div><span className="font-mono text-muted-foreground uppercase text-[9px] block mb-0.5">Notes</span>{field.notes}</div>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {fields.length === 0 && !form.name && (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-sm font-medium">No fields documented yet</p>
            <p className="text-[11px] mt-1">Add every data field: name, type, source system, target system, PHI flag, and validation rules.</p>
          </div>
        )}
      </div>
    </div>
  );
}
